import { FC, useEffect } from 'react';
import { captureException } from '@sentry/react';
import { Col, Modal, Row } from 'antd';
import { TbIcons } from 'react-icons/tb';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEditingReportComponent } from '@/hooks/useEditingReportComponent';
import { useDirtyFormDraft } from '@/hooks/useResourceDrafts';
import { normalizeReportComponentConfiguration } from '@/shared/components/modals/ResourceActionModals/EditReportComponentModal/helpers/configuration';
import { getDraftReportComponentDatasetQueryStateKey } from '@/shared/components/modals/ResourceActionModals/EditReportComponentModal/helpers/hook';
import { useReportComponentStore } from '@/stores';
import { useDatasetStore } from '@/stores/database/aliases/datasets/store';
import { ReportComponent, SetStateMethod } from '@/types';

import Preview from './layout/01-Preview';
import Settings from './layout/02-Settings';

interface EditReportComponentModalProps {
    open: boolean;
    setOpen: SetStateMethod<boolean>;
    component: ReportComponent;
}

const EditReportComponentModal: FC<EditReportComponentModalProps> = ({ open, setOpen, component }): JSX.Element => {
    const startEditingComponent = useReportComponentStore((state) => state.startEditingComponent);
    const discardDraftConfiguration = useReportComponentStore((state) => state.discardDraftConfiguration);
    const commitDraftConfiguration = useReportComponentStore((state) => state.commitDraftConfiguration);
    const invalidateComponentQuery = useDatasetStore((state) => state.invalidateComponentQuery);

    useEffect(() => {
        if (!open) {
            return;
        }

        startEditingComponent(component.id);
    }, [component.id, open, startEditingComponent]);

    const editingComponent = useEditingReportComponent(component);

    const persistedValues = {
        type: component.type,
        embedURL: component.embedURL ?? '',
        configuration: normalizeReportComponentConfiguration(component.configuration, component.type),
    };
    const draftValues = {
        type: editingComponent?.type,
        embedURL: editingComponent?.embedURL ?? '',
        configuration: editingComponent?.configuration,
    };
    const isDirty = JSON.stringify(persistedValues) !== JSON.stringify(draftValues);

    const persistDraft = async () => {
        try {
            await commitDraftConfiguration(component.id);
            invalidateComponentQuery(getDraftReportComponentDatasetQueryStateKey(component.id));
            invalidateComponentQuery(component.id);

            toast('Component Updated', {
                icon: <TbIcons size='1rem' />,
                description: editingComponent?.name ?? component.name,
            });
        } catch (error) {
            captureException(error);
            toast.error('Failed to save component');
            throw error;
        }
    };

    const { dirtyDraftModal, requestClose } = useDirtyFormDraft({
        close: () => setOpen(false),
        discard: () => discardDraftConfiguration(component.id),
        isDirty,
        save: persistDraft,
    });

    const saveAndClose = async () => {
        await persistDraft();
        setOpen(false);
    };

    const { token } = useAntDesignTheme();

    if (!editingComponent) {
        return <></>;
    }

    return (
        <Modal
            open={open}
            closable={false}
            maskClosable={false}
            keyboard={false}
            // mask={true}
            title={null}
            footer={null}
            width='min(96rem, calc(100vw - 4rem))'
            styles={{
                body: {
                    height: 'min(60rem, calc(100vh - 4rem))',
                    border: `1px solid ${token.colorBorder}`,
                    borderRadius: token.borderRadius,
                    overflow: 'hidden',
                },
                content: { padding: 0 },
                // mask: {
                //     backgroundColor: token.colorBgMask,
                //     backdropFilter: 'blur(0.25rem)',
                //     WebkitBackdropFilter: 'blur(0.125rem)',
                // },
            }}
            centered
            onCancel={requestClose}
        >
            <Row style={{ height: '100%', minHeight: 0 }}>
                <Col span={12} style={{ backgroundColor: token.colorBgContainer, height: '100%', minHeight: 0 }}>
                    <Preview component={editingComponent as ReportComponent} />
                </Col>
                <Col
                    span={12}
                    style={{
                        backgroundColor: token.colorBgContainer,
                        borderLeft: `1px solid ${token.colorBorder}`,
                        height: '100%',
                        minHeight: 0,
                    }}
                >
                    <Settings
                        component={editingComponent as ReportComponent}
                        onRequestClose={requestClose}
                        onSave={saveAndClose}
                    />
                </Col>
            </Row>
            {dirtyDraftModal}
        </Modal>
    );
};

export default EditReportComponentModal;
